import React from 'react';
import {
  AspectRatio,
  Box,
  Button,
  Link as ChakraLink,
  Stack,
  Text,
} from '@chakra-ui/react';
import { toPlainText } from '../../utils/toPlainText';

const normalizeYouTubeUrl = (url) => {
  try {
    const parsed = new URL(url);
    if (parsed.hostname.includes('youtu.be')) {
      return `https://www.youtube.com/embed/${parsed.pathname.replace('/', '')}`;
    }
    if (parsed.hostname.includes('youtube.com')) {
      const id = parsed.searchParams.get('v');
      if (id) return `https://www.youtube.com/embed/${id}`;
    }
  } catch {
    return null;
  }
  return null;
};

const instagramIframeSrc = (rawUrl) => {
  const raw = toPlainText(rawUrl);
  if (!raw) return null;
  let s = raw.trim();
  const fromHtml = s.match(/data-instgrm-permalink="([^"]+)"/i);
  if (fromHtml) s = fromHtml[1].replace(/&amp;/g, '&');
  try {
    const u = new URL(s);
    if (!u.hostname.replace(/^www\./, '').includes('instagram.com'))
      return null;
    const path = (u.pathname || '').replace(/\/+$/, '') || '';
    const reels = path.match(/^\/reels\/([^/]+)/);
    if (reels) return `https://www.instagram.com/reel/${reels[1]}/embed`;
    const reel = path.match(/^\/reel\/([^/]+)/);
    if (reel) return `https://www.instagram.com/reel/${reel[1]}/embed`;
    const post = path.match(/^\/p\/([^/]+)/);
    if (post) return `https://www.instagram.com/p/${post[1]}/embed`;
    return null;
  } catch {
    return null;
  }
};

/**
 * @param {{ provider?: string; url?: string; caption?: string; variant?: 'default'|'socialHeavy' }} props
 */
const LogbookEmbedBlock = ({ provider, url, caption, variant = 'default' }) => {
  const urlStr = toPlainText(url);
  const prov = toPlainText(provider) || 'generic';
  const cap = toPlainText(caption);
  const youtubeUrl = prov === 'youtube' ? normalizeYouTubeUrl(urlStr) : null;
  const instagramSrc = prov === 'instagram' ? instagramIframeSrc(urlStr) : null;
  const iframeSrc = youtubeUrl || instagramSrc || null;
  const isInstagram = Boolean(instagramSrc);
  const isHeavy = variant === 'socialHeavy';

  return (
    <Box
      p={isHeavy ? 5 : 4}
      border="1px solid"
      borderColor="gray.200"
      borderRadius="lg"
      bg="gray.50"
      maxW={isHeavy ? { base: '100%', md: '520px' } : undefined}
      mx={isHeavy ? 'auto' : undefined}
      my={isHeavy ? 4 : 0}
    >
      {iframeSrc ? (
        <AspectRatio
          ratio={isInstagram ? 9 / 16 : 16 / 9}
          maxW={isInstagram ? '400px' : '100%'}
          w="100%"
          mx={isInstagram ? 'auto' : undefined}
        >
          <iframe
            title={cap || 'Embedded content'}
            src={iframeSrc}
            allowFullScreen
            loading="lazy"
            referrerPolicy="strict-origin-when-cross-origin"
            allow="encrypted-media; picture-in-picture"
          />
        </AspectRatio>
      ) : (
        <Stack spacing={3}>
          <Text fontWeight="bold" color="#3b235a">
            Contenuto esterno
          </Text>
          <Text color="gray.700">
            L&apos;embed non è disponibile in questa pagina. Apri il contenuto
            sul canale originale.
          </Text>
          <Button
            as={ChakraLink}
            href={urlStr || '#'}
            isExternal
            rel="noopener noreferrer"
            target="_blank"
            bg="#36a9e1"
            color="white"
            _hover={{ bg: '#2c8bb8' }}
            w="fit-content"
          >
            Apri contenuto
          </Button>
        </Stack>
      )}
      {cap ? (
        <Text mt={3} color="gray.600" fontSize="sm">
          {cap}
        </Text>
      ) : null}
    </Box>
  );
};

export default LogbookEmbedBlock;
